import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../../hooks/useApi';
import SafeImage from '../../components/common/SafeImage';

export default function CustomerDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { getById } = useApi();
    const { data, isLoading, isError, error: fetchError } = getById('admin/customers', id);

    useEffect(() => {
        setLoading(isLoading);
        if (!isLoading && data) {
            if (data.success === true || data.status === 'success') {
                setCustomer(data.data);
                setError(null);
            } else {
                setError(data.message || 'Failed to fetch customer');
            }
        }
    }, [isLoading, data]);

    useEffect(() => {
        if (isError) {
            setError(fetchError?.message || 'Failed to fetch customer');
            setLoading(false);
        }
    }, [isError, fetchError]);

    useEffect(() => {
        // Enable Bootstrap tooltips
        if (window.bootstrap) {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.map(function (tooltipTriggerEl) {
                return new window.bootstrap.Tooltip(tooltipTriggerEl);
            });
        }
    }, [customer]);

    const getStatusBadge = (status) => {
        const statusClass = status === 'active' ? 'bg-success' : 'bg-danger';
        return <span className={`badge ${statusClass} text-capitalize`}>{status}</span>;
    };

    // Helper to get full image URL
    const getProfileImageUrl = (profileImage) => {
        if (!profileImage) return '/avatar-placeholder.png';
        if (profileImage.startsWith('/storage/')) {
            return (import.meta.env.VITE_API_URL || 'http://localhost:8000') + profileImage;
        }
        return profileImage;
    };


    if (loading) return (
        <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading customer details...</p>
        </div>
    );
    if (error) return (
        <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
            <div className="alert alert-danger p-4 rounded-4 shadow-sm text-center">
                <h4 className="mb-2"><i className="ri-error-warning-line me-2"></i>Customer Not Found</h4>
                <p>{error}</p>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/customer')}>
                    <i className="ri-arrow-left-line me-2"></i>Back to Customer List
                </button>
            </div>
        </div>
    );
    if (!customer) return <div className="container py-4">No customer found.</div>;

    return (
        <div className="container py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12 d-flex flex-wrap justify-content-between align-items-center border-bottom pb-3 mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className="ri-user-3-line text-primary fs-2"></i>
                        <h2 className="fw-bold mb-0">Customer Details</h2>
                    </div>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <Link to="/customer">Customers</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                {customer.name || 'Customer Details'}
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>
            <div className="row g-4">
                {/* Profile Card */}
                <div className="col-12 col-lg-4">
                    <div className="card rounded-4 shadow-sm border-0">
                        <div className="card-body d-flex flex-column align-items-center text-center">
                            <div className="position-relative mb-3" style={{ width: 140, height: 140 }}>
                                <SafeImage
                                    src={getProfileImageUrl(customer.profile_image)}
                                    className="img-fluid rounded-circle border border-3 border-white shadow"
                                    alt={customer.name}
                                    style={{ width: 140, height: 140, objectFit: 'cover' }}
                                />
                                {/* Pencil Icon Button for Profile Image Edit */}
                                
                                
                                <span className="position-absolute top-0 end-0 translate-middle badge rounded-pill bg-secondary shadow" style={{ fontSize: '0.95rem' }}>
                                    {getStatusBadge(customer.status)}
                                </span>
                            </div>
                            <h3 className="fw-semibold mb-1">{customer.name}</h3>
                            <div className="text-muted mb-2">Customer</div>
                            <div className="mb-2 small text-muted">
                                <span className="me-2"><i className="ri-hashtag"></i> {customer.customer_id}</span>
                            </div>
                            <div className="d-flex gap-2 mt-2">
                                
                                <Link to="/customer" className="btn btn-outline-secondary btn-sm">
                                    <i className="ri-arrow-left-line me-1"></i>Back
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Details Section */}
                <div className="col-12 col-lg-8">
                    <div className="card rounded-4 shadow-sm border-0">
                        <div className="card-body">
                            <h4 className="mb-4 text-primary fw-bold"><i className="ri-information-line me-2"></i>Customer Information</h4>
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Full Name</div>
                                        <div className="fw-semibold">{customer.name || 'N/A'}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Email</div>
                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                            <a href={`mailto:${customer.email}`} className="text-decoration-none">
                                                {customer.email || 'N/A'}
                                            </a>
                                            <button className="btn btn-link btn-sm p-0 text-primary" title="Copy Email" data-bs-toggle="tooltip" data-bs-placement="top" onClick={() => {navigator.clipboard.writeText(customer.email); toast.success('Email copied!')}}>
                                                <i className="ri-file-copy-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Phone</div>
                                        <div className="fw-semibold">
                                            <a href={`tel:${customer.phone_no}`} className="text-decoration-none">
                                                {customer.phone_no || 'N/A'}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
} 